"use client";

import React, { useRef } from "react";
import Image from "next/image";
import { useInView } from "framer-motion";
import { PortableText } from "@portabletext/react";

import Chip from "@/components/global/ui/Chip";
import { cn } from "@/lib/utils";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { FeatureItemColor, PageLPLongSchema } from "@/sanity/pages/PageLPLongTemplate";

import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackdrop from "@/components/lp-long/DotsBackdrop";
import logoApp from "@/public/lp-long/logo-app.svg";

const ICON_BG: Record<FeatureItemColor, string> = {
  green: "bg-[#D9F9B9]",
  orange: "bg-[#F9E0B9]",
  blue: "bg-[#B9C0F9]",
  purple: "bg-[#E9B9F9]",
};

// tracer colors cycle through the beam gradient stops
const TRACER_COLORS = ["#79CF22", "#AA2FD3", "#34CCE3"];

// SVG viewBox for the branch connectors (logo → each grid column)
const VB_W = 1200;
const VB_H = 143;
// horizontal box padding as a fraction of the box width (matches px-[3rem] on a 150rem box)
const PAD = 24 / 1200;

/** Curved connectors from the beam's end to the center of every grid column. */
function Branches({ cols, className }: { cols: number; className?: string }) {
  const inner = VB_W * (1 - PAD * 2);
  const paths = Array.from({ length: cols }, (_, i) => {
    const x = (VB_W * PAD + ((i + 0.5) * inner) / cols).toFixed(2);
    return `M ${VB_W / 2} 0 C ${VB_W / 2} 95, ${x} 95, ${x} ${VB_H}`;
  });

  return (
    <svg
      className={cn("lpl-branches", className)}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      aria-hidden>
      <g className="lpl-branches-base">
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
          />
        ))}
      </g>
      <g className="lpl-branches-tracer">
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
            style={
              {
                "--tracer": TRACER_COLORS[i % TRACER_COLORS.length],
                "--delay": `${(i * 0.45).toFixed(2)}s`,
              } as React.CSSProperties
            }
          />
        ))}
      </g>
    </svg>
  );
}

export default function FeaturesSection({ data, className }: { data: PageLPLongSchema; className?: string }) {
  const stackRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stackRef as React.RefObject<Element>, {
    once: true,
    margin: "-15% 0px",
  });

  const items = data?.featuresItems ?? [];

  return (
    <section
      id="features"
      className={cn("relative overflow-clip py-[6rem] lg:py-[10rem]", className)}>
      <DotsBackdrop />
      <DecorationLine
        className="top-0 hidden lg:left-[8.85%] lg:block"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 hidden lg:right-[8.85%] lg:block"
        orientation="vertical"
        style="dark"
      />

      <div className="relative container flex flex-col items-center">
        <Chip
          className="prose-p:font-geistmono prose-p:text-[#202223] prose-p:uppercase"
          image={data?.featuresChip?.icon}
          text={data?.featuresChip?.text}
        />

        {/* prose-headings:text-[4rem] prose-headings:leading-[1.1] lg:prose-headings:text-[6.5rem] */}
        {data?.featuresTitle && (
          <div className="richtext w-[min(73.75rem,100%)] prose-headings:text-[4rem] lg:prose-headings:text-[6.5rem]">
            <PortableText
              value={data.featuresTitle}
              components={richTextComponent}
            />
          </div>
        )}

        <div
          ref={stackRef}
          className={cn(
            "lpl-stack relative mt-[3rem] flex w-full flex-col items-center lg:mt-[4rem]",
            inView && "is-visible",
          )}>
          {/* app icon + label */}
          <div className="relative z-[2] flex flex-col items-center gap-[1.25rem]">
            <span
              className="lpl-stack-glow"
              aria-hidden
            />
            <Image
              className="relative !w-[10.25rem]"
              src={logoApp}
              alt=""
            />
            {data?.featuresLogoLabel && (
              <p className="relative mb-0 font-geist text-[2.25rem] leading-[1.4] font-semibold tracking-[-0.035em] text-black">
                {data.featuresLogoLabel}
              </p>
            )}
          </div>

          {/* vertical beam */}
          <div
            className="lpl-stack-beam relative z-[2] mt-[.5rem] h-[7rem] w-[2px]"
            aria-hidden>
            <span className="lpl-stack-beam-line" />
            <span className="lpl-stack-beam-pulse" />
          </div>

          {/* capabilities box */}
          <div className="lpl-stack-box relative mt-[5.5rem] w-[min(150rem,100%)] rounded-[2rem] border border-white/60 bg-white px-[1.5rem] py-[2.5rem] shadow-[inset_0_1px_0_rgba(255,255,255,.7),0_22px_50px_rgba(15,15,15,.06)] lg:rounded-[3.5rem] lg:px-[3rem] lg:py-[3rem]">
            <Branches
              cols={3}
              className="md:hidden"
            />
            <Branches
              cols={4}
              className="hidden md:block lg:hidden"
            />
            <Branches
              cols={7}
              className="hidden lg:block"
            />

            <ul className="relative z-[1] flex flex-wrap justify-center gap-x-[1rem] gap-y-[2rem] lg:gap-x-[1.5rem] lg:gap-y-[2.875rem]">
              {items.map((item, i) => (
                <li
                  key={i}
                  className="flex w-[calc((100%-2rem)/3)] min-h-[14.5rem] flex-col items-center justify-center gap-[1.5rem] rounded-[1.75rem] p-[1.5rem] text-center transition-colors duration-300 hover:bg-[#F3F2F1] md:w-[calc((100%-3rem)/4)] lg:w-[calc((100%-9rem)/7)]">
                  <span
                    className={cn(
                      "flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-[.5rem]",
                      ICON_BG[item?.color] ?? ICON_BG.green,
                    )}>
                    {item?.icon && (
                      <Image
                        className="!w-[3rem]"
                        src={item.icon}
                        alt=""
                      />
                    )}
                  </span>
                  <p className="mb-0 font-geist text-[1.75rem] leading-[1.4] font-semibold tracking-[-0.035em] text-black">
                    {item?.text}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
